/* eslint-disable i18next/no-literal-string */
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {restrictToFirstScrollableAncestor} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {Button} from '@dropbox/dig-components/buttons';
import {FormHelperText, FormLabel, FormRow} from '@dropbox/dig-components/form_row';
import {Modal} from '@dropbox/dig-components/modal';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {Select, TextInput} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AddLine, DeleteLine, DragHandleLine} from '@dropbox/dig-icons/assets';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {snackbarAtom} from 'atoms/snackbar';
import classNames from 'classnames';
import {AdminKnowledgeLinksService, HomeService, KnowledgeLink} from 'client';
import {CancelButton} from 'components/DSYS/CancelButton';
import {useSetAtom} from 'jotai';
import {useEffect, useMemo, useRef, useState} from 'react';
import {getService} from 'utilities';
import {getKnowledgeLinkIcon} from 'views/home/sections/Search';

import styles from './AdminKnowledgeLinks.module.css';

const KNOWLEDGE_LINKS_QUERY_KEY = ['admin', 'knowledge-links'] as const;

type EditableKnowledgeLink = Omit<KnowledgeLink, 'created_at' | 'updated_at'>;

const LINK_TYPE_OPTIONS: Array<{label: string; value: KnowledgeLink.link_type}> = [
  {label: 'CompanyOS link', value: KnowledgeLink.link_type.URL},
  {label: 'External link', value: KnowledgeLink.link_type.EXTERNAL_URL},
  {label: 'Ask query', value: KnowledgeLink.link_type.ASK_QUERY},
];

const getLinkTypeLabel = (linkType: KnowledgeLink.link_type) =>
  LINK_TYPE_OPTIONS.find(({value}) => value === linkType)?.label ?? linkType;

const withSortOrder = (links: EditableKnowledgeLink[]) =>
  links.map((link, index) => ({...link, sort_order: index + 1}));

const getUniqueChipType = (links: EditableKnowledgeLink[], baseChipType: string) => {
  const chipTypes = new Set(links.map(({chip_type}) => chip_type.trim()));
  if (!chipTypes.has(baseChipType)) {
    return baseChipType;
  }

  let suffix = 2;
  while (chipTypes.has(`${baseChipType}_${suffix}`)) {
    suffix += 1;
  }
  return `${baseChipType}_${suffix}`;
};

const SortableKnowledgeLink = ({
  link,
  isSelected,
  dragDisabled,
  showDragHandle,
  onSelect,
}: {
  link: EditableKnowledgeLink;
  isSelected: boolean;
  dragDisabled: boolean;
  showDragHandle: boolean;
  onSelect: () => void;
}) => {
  const {attributes, listeners, isDragging, isSorting, setNodeRef, transform, transition} =
    useSortable({id: link.id, disabled: dragDisabled});

  return (
    <div
      ref={setNodeRef}
      className={classNames(styles.linkRow, {
        [styles.linkRowWithoutDragHandle]: !showDragHandle,
        [styles.selectedLinkRow]: isSelected,
      })}
      style={{
        transition: transition ?? undefined,
        transform: `translate3d(${transform?.x ?? 0}px, ${transform?.y ?? 0}px, 0)`,
        zIndex: isDragging ? 1 : undefined,
        opacity: isSorting && !isDragging ? 0.7 : 1,
      }}
    >
      {showDragHandle ? (
        <button
          type="button"
          aria-label={`Reorder ${link.title}`}
          className={styles.dragHandle}
          disabled={dragDisabled}
          {...attributes}
          {...listeners}
        >
          <UIIcon
            src={DragHandleLine}
            className={atoms({color: isDragging ? 'Border Base' : 'Border Subtle'})}
          />
        </button>
      ) : (
        <span className={styles.dragHandlePlaceholder} aria-hidden="true" />
      )}
      <button
        type="button"
        className={styles.linkSelectButton}
        aria-pressed={isSelected}
        onClick={onSelect}
      >
        <UIIcon src={getKnowledgeLinkIcon(link.icon)} className={atoms({color: 'Text Subtle'})} />
        <span className={styles.linkRowText}>
          <Text isBold>{`${link.id < 0 ? '[Draft] ' : ''}${link.title || 'Untitled link'}`}</Text>
          <Text size="small" color="subtle">
            {getLinkTypeLabel(link.link_type)}
          </Text>
        </span>
      </button>
    </div>
  );
};

const KnowledgeLinkForm = ({
  link,
  existingChipTypes,
  onSave,
  onRemove,
  isSaving,
}: {
  link: EditableKnowledgeLink;
  existingChipTypes: string[];
  onSave: (link: EditableKnowledgeLink) => void;
  onRemove: () => void;
  isSaving: boolean;
}) => {
  const [draft, setDraft] = useState(link);

  useEffect(() => {
    setDraft(link);
  }, [link]);

  const titleIsInvalid = !draft.title.trim();
  const normalizedChipType = draft.chip_type.trim();
  const chipTypeIsEmpty = !normalizedChipType;
  const chipTypeIsDuplicate = existingChipTypes.includes(normalizedChipType);
  const chipTypeIsInvalid = chipTypeIsEmpty || chipTypeIsDuplicate;
  const normalizedUrl = draft.url.trim();
  const urlIsRequired = draft.link_type !== KnowledgeLink.link_type.ASK_QUERY;
  const urlIsEmpty = urlIsRequired && !normalizedUrl;
  const urlHasInvalidFormat =
    (draft.link_type === KnowledgeLink.link_type.URL &&
      Boolean(normalizedUrl) &&
      !normalizedUrl.startsWith('/')) ||
    (draft.link_type === KnowledgeLink.link_type.EXTERNAL_URL &&
      Boolean(normalizedUrl) &&
      !normalizedUrl.startsWith('https://'));
  const urlIsInvalid = urlIsEmpty || urlHasInvalidFormat;
  const isDirty =
    draft.title !== link.title ||
    draft.icon !== link.icon ||
    draft.chip_type !== link.chip_type ||
    draft.link_type !== link.link_type ||
    draft.url !== link.url;
  const canSave = isDirty && !titleIsInvalid && !chipTypeIsInvalid && !urlIsInvalid;

  const resetDraft = () => setDraft(link);

  return (
    <Stack gap="24">
      <Box className={styles.editorHeader} style={{alignItems: 'start'}}>
        <Box display="flex" alignItems="center" style={{gap: 12}}>
          <Box className={styles.editorIcon}>
            <UIIcon src={getKnowledgeLinkIcon(draft.icon)} />
          </Box>
          <Stack display="flex" gap="2">
            <Text variant="paragraph" size="xlarge" isBold>
              {draft.title || 'Untitled link'}
            </Text>
            <Text size="small" color="subtle">
              Display position {link.sort_order}
            </Text>
          </Stack>
        </Box>
        <Button
          variant="opacity"
          size="small"
          withIconStart={<UIIcon src={DeleteLine} />}
          onClick={onRemove}
          disabled={isSaving}
        >
          Remove link
        </Button>
      </Box>

      <div className={styles.form}>
        <FormRow>
          <FormLabel htmlFor={`knowledge-link-title-${link.id}`}>Title</FormLabel>
          <FormHelperText>Label displayed in Home knowledge chip</FormHelperText>
          <TextInput
            id={`knowledge-link-title-${link.id}`}
            value={draft.title}
            isInvalid={titleIsInvalid}
            onChange={(event) => setDraft({...draft, title: event.currentTarget.value})}
          />
          {titleIsInvalid ? (
            <Text size="small" className={atoms({color: 'Alert Base'})}>
              Enter a title.
            </Text>
          ) : null}
        </FormRow>

        <FormRow>
          <FormLabel htmlFor={`knowledge-link-icon-${link.id}`}>Icon</FormLabel>
          <FormHelperText>
            Enter DIG name for icon displayed before chip label, such as HeartLine
          </FormHelperText>
          <TextInput
            id={`knowledge-link-icon-${link.id}`}
            value={draft.icon}
            placeholder="HeartLine"
            onChange={(event) => setDraft({...draft, icon: event.currentTarget.value})}
          />
        </FormRow>

        <FormRow>
          <FormLabel htmlFor={`knowledge-link-chip-type-${link.id}`}>Logging chip type</FormLabel>
          <FormHelperText>Stable identifier used for analytics</FormHelperText>
          <TextInput
            id={`knowledge-link-chip-type-${link.id}`}
            value={draft.chip_type}
            isInvalid={chipTypeIsInvalid}
            onChange={(event) => setDraft({...draft, chip_type: event.currentTarget.value})}
          />
          {chipTypeIsEmpty ? (
            <Text size="small" className={atoms({color: 'Alert Base'})}>
              Enter logging chip type
            </Text>
          ) : chipTypeIsDuplicate ? (
            <Text size="small" className={atoms({color: 'Alert Base'})}>
              This logging chip type is already in use
            </Text>
          ) : null}
        </FormRow>

        <FormRow>
          <FormLabel htmlFor={`knowledge-link-link-type-${link.id}`}>Link type</FormLabel>
          <FormHelperText>Controls how chip opens</FormHelperText>
          <Select
            id={`knowledge-link-link-type-${link.id}`}
            value={draft.link_type}
            onChange={(linkType) =>
              setDraft({...draft, link_type: linkType as KnowledgeLink.link_type})
            }
          >
            {LINK_TYPE_OPTIONS.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </FormRow>

        <FormRow>
          <FormLabel htmlFor={`knowledge-link-url-${link.id}`}>URL</FormLabel>
          <FormHelperText>
            {draft.link_type === KnowledgeLink.link_type.ASK_QUERY
              ? 'Ask query chips do not use a URL'
              : draft.link_type === KnowledgeLink.link_type.EXTERNAL_URL
                ? 'Enter a complete URL, including https://'
                : 'Enter a CompanyOS path beginning with /'}
          </FormHelperText>
          <TextInput
            id={`knowledge-link-url-${link.id}`}
            value={draft.url}
            isInvalid={urlIsInvalid}
            disabled={!urlIsRequired}
            placeholder={
              draft.link_type === KnowledgeLink.link_type.EXTERNAL_URL
                ? 'https://example.com'
                : '/content/...'
            }
            onChange={(event) => setDraft({...draft, url: event.currentTarget.value})}
          />
          {urlIsEmpty ? (
            <Text size="small" className={atoms({color: 'Alert Base'})}>
              Enter a URL
            </Text>
          ) : urlHasInvalidFormat ? (
            <Text size="small" className={atoms({color: 'Alert Base'})}>
              {draft.link_type === KnowledgeLink.link_type.EXTERNAL_URL
                ? 'External links must begin with https://'
                : 'CompanyOS links must begin with /'}
            </Text>
          ) : null}
        </FormRow>
      </div>

      <Box className={styles.formActions}>
        <CancelButton onClick={resetDraft} disabled={!isDirty}>
          Cancel
        </CancelButton>
        <Button
          variant="primary"
          disabled={!canSave || isSaving}
          isLoading={isSaving}
          onClick={() =>
            onSave({
              ...draft,
              title: draft.title.trim(),
              icon: draft.icon.trim(),
              chip_type: draft.chip_type.trim(),
              url: draft.link_type === KnowledgeLink.link_type.ASK_QUERY ? '' : draft.url.trim(),
            })
          }
        >
          Save changes
        </Button>
      </Box>
    </Stack>
  );
};

const KnowledgeLinksEditorContent = ({initialLinks}: {initialLinks: KnowledgeLink[]}) => {
  const queryClient = useQueryClient();
  const setSnackbar = useSetAtom(snackbarAtom);
  const nextDraftId = useRef(-1);
  const [links, setLinks] = useState<EditableKnowledgeLink[]>(() => [...initialLinks]);
  const [selectedId, setSelectedId] = useState<number | null>(initialLinks[0]?.id ?? null);
  const [pendingRemoval, setPendingRemoval] = useState<EditableKnowledgeLink | null>(null);

  const removeLinkLocally = (linkToRemove: EditableKnowledgeLink) => {
    const removalIndex = links.findIndex(({id}) => id === linkToRemove.id);
    const nextLinks = withSortOrder(links.filter(({id}) => id !== linkToRemove.id));
    const nextSelectedLink = nextLinks[Math.min(removalIndex, nextLinks.length - 1)] ?? null;
    setLinks(nextLinks);
    setSelectedId(nextSelectedLink?.id ?? null);
    setPendingRemoval(null);
    queryClient.setQueryData<KnowledgeLink[]>(KNOWLEDGE_LINKS_QUERY_KEY, (current = []) =>
      current.filter(({id}) => id !== linkToRemove.id)
    );
  };

  const saveMutation = useMutation({
    mutationFn: (link: EditableKnowledgeLink) => {
      const requestBody = {
        icon: link.icon,
        title: link.title,
        chip_type: link.chip_type,
        link_type: link.link_type,
        url: link.url,
        sort_order: link.sort_order,
      };

      if (link.id < 0) {
        return getService(
          AdminKnowledgeLinksService
        ).createKnowledgeLinkApiV1AdminKnowledgeLinksPost(requestBody);
      }

      return getService(
        AdminKnowledgeLinksService
      ).updateKnowledgeLinkApiV1AdminKnowledgeLinksLinkIdPut(link.id, requestBody);
    },
    onSuccess: (savedLink, submittedLink) => {
      setLinks((current) =>
        current.map((link) => (link.id === submittedLink.id ? savedLink : link))
      );
      setSelectedId(savedLink.id);
      queryClient.setQueryData<KnowledgeLink[]>(KNOWLEDGE_LINKS_QUERY_KEY, (current = []) =>
        submittedLink.id < 0
          ? [...current, savedLink]
          : current.map((link) => (link.id === submittedLink.id ? savedLink : link))
      );
      setSnackbar({text: 'Saved.'});
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (link: EditableKnowledgeLink) =>
      getService(
        AdminKnowledgeLinksService
      ).deleteKnowledgeLinkApiV1AdminKnowledgeLinksLinkIdDelete(link.id),
    onSuccess: (_data, deletedLink) => removeLinkLocally(deletedLink),
  });

  const reorderMutation = useMutation({
    mutationFn: ({
      newLinks,
    }: {
      newLinks: EditableKnowledgeLink[];
      previousLinks: EditableKnowledgeLink[];
    }) =>
      getService(
        AdminKnowledgeLinksService
      ).reorderKnowledgeLinksApiV1AdminKnowledgeLinksReorderPut(
        newLinks.map(({id, sort_order}) => ({id, sort_order}))
      ),
    onSuccess: (reorderedLinks) => {
      setLinks(reorderedLinks);
      queryClient.setQueryData(KNOWLEDGE_LINKS_QUERY_KEY, reorderedLinks);
      setSnackbar({text: 'Saved.'});
    },
    onError: (_error, {previousLinks}) => setLinks(previousLinks),
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
  );

  const selectedLink = useMemo(
    () => links.find(({id}) => id === selectedId) ?? null,
    [links, selectedId]
  );
  const configuredLinkCount = links.filter(({id}) => id > 0).length;
  const draftLinkCount = links.length - configuredLinkCount;
  const hasDraftLink = links.some(({id}) => id < 0);

  const handleDragEnd = ({active, over}: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = links.findIndex(({id}) => id === active.id);
    const newIndex = links.findIndex(({id}) => id === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    const newLinks = withSortOrder(arrayMove(links, oldIndex, newIndex));
    setLinks(newLinks);
    reorderMutation.mutate({newLinks, previousLinks: links});
  };

  const handleAdd = () => {
    saveMutation.reset();
    const draftId = nextDraftId.current;
    nextDraftId.current -= 1;

    const nextLink: EditableKnowledgeLink = {
      id: draftId,
      icon: 'LinkLine',
      title: `New knowledge link ${Math.abs(draftId)}`,
      chip_type: getUniqueChipType(links, 'new_knowledge_link'),
      link_type: KnowledgeLink.link_type.URL,
      url: '/content',
      sort_order: links.length + 1,
    };
    setLinks((current) => [...current, nextLink]);
    setSelectedId(draftId);
  };

  const handleSave = (updatedLink: EditableKnowledgeLink) => {
    saveMutation.mutate(updatedLink);
  };

  const handleRemove = () => {
    if (!pendingRemoval) {
      return;
    }

    if (pendingRemoval.id < 0) {
      removeLinkLocally(pendingRemoval);
      return;
    }

    deleteMutation.mutate(pendingRemoval);
  };

  const closeRemoveDialog = () => {
    if (deleteMutation.isPending) {
      return;
    }

    deleteMutation.reset();
    setPendingRemoval(null);
  };

  return (
    <Stack gap="16" className={atoms({paddingTop: '24'})}>
      {saveMutation.isError || deleteMutation.isError || reorderMutation.isError ? (
        <SystemBanner tone="alert" role="alert">
          <Text>
            {deleteMutation.isError
              ? 'Unable to remove knowledge link. Try again.'
              : reorderMutation.isError
                ? 'Unable to reorder knowledge links. Try again.'
                : 'Unable to save knowledge link. Try again.'}
          </Text>
        </SystemBanner>
      ) : null}

      <div className={styles.workspace}>
        <section className={styles.sidebar} aria-label="Knowledge links">
          <Box className={styles.sidebarHeader}>
            <Stack display="flex" gap="2">
              <Text isBold>Knowledge links</Text>
              <Text size="small" color="subtle">
                <span>{configuredLinkCount} configured</span>
                {draftLinkCount > 0 && (
                  <>
                    , <span>{draftLinkCount} draft</span>
                  </>
                )}
              </Text>
            </Stack>
            <Button
              variant="opacity"
              size="small"
              withIconStart={<UIIcon src={AddLine} />}
              onClick={handleAdd}
            >
              Add
            </Button>
          </Box>

          <div className={styles.linkList}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToFirstScrollableAncestor]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={links.map(({id}) => id)}
                strategy={verticalListSortingStrategy}
              >
                {links.map((link) => (
                  <SortableKnowledgeLink
                    key={link.id}
                    link={link}
                    isSelected={link.id === selectedId}
                    dragDisabled={links.length < 2 || reorderMutation.isPending}
                    showDragHandle={!hasDraftLink}
                    onSelect={() => setSelectedId(link.id)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        </section>

        <section className={styles.editorPanel} aria-label="Knowledge link details">
          {selectedLink ? (
            <KnowledgeLinkForm
              key={selectedLink.id}
              link={selectedLink}
              existingChipTypes={links
                .filter(({id}) => id !== selectedLink.id)
                .map(({chip_type}) => chip_type.trim())}
              onSave={handleSave}
              onRemove={() => {
                deleteMutation.reset();
                setPendingRemoval(selectedLink);
              }}
              isSaving={saveMutation.isPending && saveMutation.variables.id === selectedLink.id}
            />
          ) : (
            <Box className={styles.emptyState}>
              <Text isBold>No knowledge links</Text>
              <Text size="small" color="subtle">
                Add a link to configure a Home knowledge chip.
              </Text>
              <Button variant="outline" size="small" onClick={handleAdd}>
                Add knowledge link
              </Button>
            </Box>
          )}
        </section>
      </div>

      {pendingRemoval ? (
        <Modal
          open
          width="small"
          withCloseButton="close"
          aria-label="Remove knowledge link?"
          onRequestClose={closeRemoveDialog}
        >
          <Modal.Header>
            <Modal.Title>Remove knowledge link?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            “{pendingRemoval.title}” will be removed from Home knowledge chips.
          </Modal.Body>
          <Modal.Footer>
            <CancelButton onClick={closeRemoveDialog} disabled={deleteMutation.isPending}>
              Cancel
            </CancelButton>
            <Button
              variant="primary"
              onClick={handleRemove}
              disabled={deleteMutation.isPending}
              isLoading={deleteMutation.isPending}
            >
              Remove
            </Button>
          </Modal.Footer>
        </Modal>
      ) : null}
    </Stack>
  );
};

export const KnowledgeLinksEditor = () => {
  const {data, isError, isLoading} = useQuery({
    queryKey: KNOWLEDGE_LINKS_QUERY_KEY,
    queryFn: () => getService(HomeService).knowledgeLinksApiV1HomeKnowledgeLinksGet(),
  });

  if (isLoading) {
    return (
      <Box paddingTop="24">
        <Spinner size="small" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box className={atoms({paddingTop: '24'})}>
        <SystemBanner tone="alert" role="alert">
          <Text>Unable to load knowledge links. Try refreshing the page.</Text>
        </SystemBanner>
      </Box>
    );
  }

  return <KnowledgeLinksEditorContent initialLinks={data ?? []} />;
};
